"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.cro = cro;
/* I needed separate files to store types for Optimizely. I used to store them
in a .d.ts file in types/ and, although that technically works (and is how I
would prefer to keep it), it causes type errors on the compiled dist/src/*.d.ts
files that don't get caught until this package gets installed on another repo
and its type checking flags it. Storing them in .ts modules causes them to
build properly. */

function cro() {
  const TOOL_NAME = "optimizely";
  window[TOOL_NAME] = window[TOOL_NAME] || [];

  /** Enables or disables developer mode */
  function becomeCrow(wantsToBeCrow = true) {
    if (wantsToBeCrow) {
      setUserAttributes({
        isCrow: "true"
      });
      console.info("You are now a CROw tester.");
    } else {
      setUserAttributes({
        isCrow: null
      });
      console.info("You are no longer a CROw tester.");
    }
  }

  /** Waits until `condition()` returns `true` (or it times out). */
  function waitUntil(condition, options = {
    interval: 100
  }) {
    return new Promise((resolve, reject) => {
      let timeout = 0;
      const interval = window.setInterval(() => {
        if (condition()) {
          window.clearTimeout(timeout);
          window.clearInterval(interval);
          resolve(true);
        }
      }, options.interval);
      if (options.timeout) {
        timeout = window.setTimeout(() => {
          window.clearInterval(interval);
          reject("Timed out.");
        }, options.timeout);
      }
    });
  }

  /* Functions are ordered the same as in the Optimizely documentation.
  Exports are ordered alphabetically at the bottom of the file if that's
  more helpful :) optimizely.d.ts has a similar layout. */

  /**
   * Re-initializes the snippet. You might want this if your website is an
   * SPA. Otherwise, avoid calling this function.
   *
   * https://docs.developers.optimizely.com/web-experimentation/reference/activate
   */
  function reload() {
    window[TOOL_NAME].push({
      type: "activate"
    });
  }

  /**
   * Send a conversion event. If the both the `value`/`revenue` argument and
   * tag of the same name are provided, the argument overwrites the tag.
   *
   * - `value` is not necessarily a dollar amount.
   * - `revenue` must be in dollars. See [how AoPS calculates revenue](https://docs.google.com/document/d/1JraaYEqe4tV9vscnan60DBLSCg-o1N8_RO1wDqDts2k/edit#heading=h.73dosbglyion).
   *
   * https://docs.developers.optimizely.com/web-experimentation/reference/event
   */
  function sendEvent(eventData) {
    const {
      name,
      value = 0,
      tags = {},
      revenue = 0
    } = eventData;
    if (revenue) {
      tags.revenue = revenue;
    }
    if (value) {
      tags.value = value;
    }
    if (tags.revenue) {
      tags.revenue = Math.round(tags.revenue * 100);
    }
    window[TOOL_NAME].push({
      type: "event",
      eventName: name,
      tags
    });
  }

  /**
   * Some things that don't like iframes might need this.
   *
   * https://docs.developers.optimizely.com/web-experimentation/reference/disable-cross-origin
   */
  function disableCrossOriginTracking() {
    window[TOOL_NAME].push({
      type: "disableCrossOrigin"
    });
  }

  /**
   * Disables CRO entirely.
   *
   * https://docs.developers.optimizely.com/web-experimentation/reference/disable
   */
  function disable() {
    window[TOOL_NAME].push({
      type: "disable"
    });
  }

  /**
   * Disables tracking only.
   *
   * https://docs.developers.optimizely.com/web-experimentation/reference/disable#parameters
   */
  function disableTracking() {
    window[TOOL_NAME].push({
      type: "disable",
      scope: "tracking"
    });
  }

  /**
   * Holds onto events instead of sending them ASAP. Call `forceSync()` to
   * send them at a specific time.
   *
   * https://docs.developers.optimizely.com/web-experimentation/reference/hold-events
   */
  function enableEventQueue() {
    window[TOOL_NAME].push({
      type: "holdEvents"
    });
  }

  /**
   * Enables or disables logging for the snippet.
   *
   * @param level "OFF" (default) | "ERROR" | "WARN" | "INFO" | "DEBUG"
   *
   * https://docs.developers.optimizely.com/web-experimentation/reference/log
   */
  function setLogLevel(level) {
    window[TOOL_NAME].push({
      type: "log",
      level
    });
  }

  /**
   * Trigger a pageview. This is useful for SPAs, sites with dynamic content,
   * or pages with complicated URLs to change context for both tracking and
   * targeting for CRO experiments. Tags attached to the pageview apply to
   * all events in the new context.
   *
   * https://docs.developers.optimizely.com/web-experimentation/reference/page
   */
  function triggerPageview(pageName, tags = {}) {
    window[TOOL_NAME].push({
      type: "page",
      pageName,
      tags
    });
  }

  /**
   * Send all of the enqueued events to the CRO tool's server.
   *
   * https://docs.developers.optimizely.com/web-experimentation/reference/send-events
   */
  function forceSync() {
    window[TOOL_NAME].push({
      type: "sendEvents"
    });
  }

  /**
   * Provide information about the user to the CRO tool. Do not send PII!
   * Do not pass GO! Do not collect $200! Please hash sensitive information!
   * Marketing uses this on experiments to target relevant audiences and
   * segment results for detailed analysis. User attributes persist in
   * `localStorage`. Setting an attribute to `null` removes it.
   *
   * https://docs.developers.optimizely.com/web-experimentation/reference/user
   */
  function setUserAttributes(attributes) {
    window[TOOL_NAME].push({
      type: "user",
      attributes
    });
  }

  /**
   * Attach tags to the current context. These work the same as the tags for
   * `triggerPageview()` but this can be called at any time.
   *
   * https://docs.developers.optimizely.com/web-experimentation/reference/tags
   */
  function attachTags(tags) {
    window[TOOL_NAME].push({
      type: "tags",
      tags
    });
  }

  /**
   * Retrieves a part of the Optimizely snippet. Avoid calling
   * `optimizely.get()` directly, because it may not exist!
   *
   * https://docs.developers.optimizely.com/web-experimentation/reference/get
   */
  function optimizelyGet(type) {
    if (Array.isArray(window[TOOL_NAME])) {
      return;
    }
    return window[TOOL_NAME].get(type);
  }

  /**
   * Get the state of the current user.
   *
   * https://docs.developers.optimizely.com/web-experimentation/reference/visitor
   */
  function getVisitor() {
    const visitor = optimizelyGet("visitor");
    if (!visitor) {
      return;
    }
    return visitor;
  }

  /**
   * Get the ID assigned to the current user by the CRO tool.
   *
   * https://docs.developers.optimizely.com/web-experimentation/reference/visitor-id
   */
  function getVisitorId() {
    const visitorId = optimizelyGet("visitor_id");
    if (!visitorId) {
      return;
    }
    return visitorId.randomId;
  }
  return {
    attachTags,
    becomeCrow,
    disable,
    disableCrossOriginTracking,
    disableTracking,
    enableEventQueue,
    forceSync,
    getVisitor,
    getVisitorId,
    reload,
    sendEvent,
    setLogLevel,
    setUserAttributes,
    triggerPageview,
    waitUntil
  };
}
//# sourceMappingURL=cro.js.map